import useMobileView from '../../../hooks/useMobileView';
import { useDOMPurify } from '../../../lib/useDOMPurify';
import { RichText } from '../atoms/RichText';
import { Title } from '../atoms/Title';
import { AsideNavigationBlock } from '../organisms/AsideNavigationBlock';
import { ContentArea } from '../organisms/ContentArea';

import { useQuery } from '@tanstack/react-query';
import { getContentWithChildren } from '../../../api/contentDeliveryApi';
import { KnowledgeArticlePage } from '../../../types/optimizely/knowledgeArticlePage';
import { useNavigationData } from '../../../hooks/useNavigationData';
import ScrollToHashElement from '../../../lib/ScrollIntoHashElement';
import { LanguageSwitcherItemsHandler } from '../../../lib/LanguageSwitcherItemsHandler';

export const KnowledgeBaseArticlePage = ({
	pageId,
	language,
}: {
	pageId: string;
	language: string;
}) => {
	const content = useQuery<KnowledgeArticlePage>({
		queryKey: [pageId],
		queryFn: async () =>
			getContentWithChildren({ contentId: pageId, lang: language }),
		refetchOnMount: false,
	});

	const { navigationData, isEpiEdit } = useNavigationData();
	const isMobile = useMobileView(900);

	const {
		mainContentArea,
		topContentArea,
		bottomContentArea,
		title,
		description,
	} = content?.data || {};

	const sanitizedDescription = useDOMPurify(description?.value || '');

	if (content.isLoading) {
		return <div>Loading...</div>;
	}

	if (content.isError) {
		return <div>Error</div>;
	}

	if (
		!content.data ||
		!mainContentArea ||
		!topContentArea ||
		!bottomContentArea
	) {
		return <div>No data</div>;
	}

	const mainClassNames = `max-w-[1160px] mx-auto px-5 xl:px-0`;
	const contentAreaClassNames = `flex ${isMobile ? 'pt-8' : 'pt-5'}`;

	return (
		<>
			<LanguageSwitcherItemsHandler pageTitle={content.data.name} />
			<div>
				<ScrollToHashElement behavior="smooth" />
				{!isEpiEdit && isMobile ? (
					<aside style={{ display: 'unset' }}>
						{<AsideNavigationBlock naviData={navigationData} />}
					</aside>
				) : null}
				<div className={mainClassNames}>
					{title?.value || sanitizedDescription ? (
						<header className="pt-16 xl:pt-28">
							{title?.value ? (
								<Title title={title.value || ''} style="h1" />
							) : null}
							{sanitizedDescription ? (
								<RichText
									richText={sanitizedDescription}
									className="pt-4 text-md"
								/>
							) : null}
						</header>
					) : null}
					{topContentArea.value ? (
						<section className={contentAreaClassNames}>
							<div>
								<ContentArea
									key={pageId + '-topContentArea'}
									contentArea={topContentArea}
								/>
							</div>
						</section>
					) : null}
					<section className={contentAreaClassNames}>
						{!isEpiEdit && !isMobile ? (
							<aside>
								<AsideNavigationBlock naviData={navigationData} />
							</aside>
						) : null}
						<div className="max-w-full">
							<ContentArea
								key={pageId + '-mainContentArea'}
								contentArea={mainContentArea}
								containerClasses={isMobile ? '' : 'pl-5'}
							/>
						</div>
					</section>
					{bottomContentArea.value ? (
						<section className={`${contentAreaClassNames} justify-center`}>
							<div>
								<ContentArea
									key={pageId + '-bottomContentArea'}
									contentArea={bottomContentArea}
								/>
							</div>
						</section>
					) : null}
				</div>
			</div>
		</>
	);
};
